/**
 * Raw FHIR write helpers that bypass MedplumClient's `credentials: 'include'`.
 *
 * The Medplum SDK sets `credentials: 'include'` on every fetch, which causes
 * the browser to send cookies for the API origin. When the user has previously
 * accessed the Medplum admin app (same origin, different port path), those
 * session cookies can override the Bearer-token identity and the server resolves
 * a different access policy — producing 403 on writes that rely on parameterised
 * compartment criteria like `%care_team`.
 *
 * These helpers make raw fetch calls with only the Bearer token (no cookies) so
 * access policy resolution always matches the signed-in user.
 */
import type { DocumentReference, QuestionnaireResponse, Resource } from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';

import { withUnifyProfile } from '@unify/fhir-model/profiles';

function baseUrl(medplum: MedplumClient): string {
  return medplum.getBaseUrl().replace(/\/$/, '');
}

function authHeaders(medplum: MedplumClient): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/fhir+json',
    Accept: 'application/fhir+json',
  };
  const token = medplum.getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function fhirCreateQR(
  medplum: MedplumClient,
  resource: QuestionnaireResponse,
): Promise<QuestionnaireResponse> {
  const res = await fetch(`${baseUrl(medplum)}/fhir/R4/QuestionnaireResponse`, {
    method: 'POST',
    headers: authHeaders(medplum),
    body: JSON.stringify(withUnifyProfile(resource)),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`FHIR create failed (${res.status}): ${body.slice(0, 400)}`);
  }
  return res.json();
}

export async function fhirUpdateQR(
  medplum: MedplumClient,
  resource: QuestionnaireResponse & { id: string },
): Promise<QuestionnaireResponse> {
  const res = await fetch(`${baseUrl(medplum)}/fhir/R4/QuestionnaireResponse/${resource.id}`, {
    method: 'PUT',
    headers: authHeaders(medplum),
    body: JSON.stringify(withUnifyProfile(resource)),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`FHIR update failed (${res.status}): ${body.slice(0, 400)}`);
  }
  return res.json();
}

/**
 * Create DocumentReference with Bearer-only auth (no cookies).
 * Same rationale as {@link fhirCreateQR} — avoids 403 when Medplum admin cookies
 * override the SPA token on `credentials: 'include'` requests.
 */
/** PUT any FHIR resource by type/id with Bearer-only auth (no cookies). */
export async function fhirPutResource<T extends Resource & { id: string }>(
  medplum: MedplumClient,
  resource: T,
): Promise<T> {
  const res = await fetch(`${baseUrl(medplum)}/fhir/R4/${resource.resourceType}/${resource.id}`, {
    method: 'PUT',
    headers: authHeaders(medplum),
    body: JSON.stringify(withUnifyProfile(resource)),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`FHIR update failed (${res.status}): ${body.slice(0, 400)}`);
  }
  return res.json() as Promise<T>;
}

export async function fhirCreateDocumentReference(
  medplum: MedplumClient,
  resource: DocumentReference,
): Promise<DocumentReference> {
  const res = await fetch(`${baseUrl(medplum)}/fhir/R4/DocumentReference`, {
    method: 'POST',
    headers: authHeaders(medplum),
    body: JSON.stringify(withUnifyProfile(resource)),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`FHIR DocumentReference create failed (${res.status}): ${body.slice(0, 400)}`);
  }
  return res.json() as Promise<DocumentReference>;
}
